/**
 * OneX Champions — leaderboard with podium top-3, confetti, animated avatars.
 *
 * Layout:
 *   ┌─────────────────────────────────────────────────────────────────┐
 *   │ 🎉 Confetti backdrop (24 lime dots falling on infinite loop)    │
 *   │                                                                 │
 *   │        Hero copy + "Your standing" pill (sticky right)          │
 *   │                                                                 │
 *   │        ┌──────────────────────────────────┐                     │
 *   │        │  Top 3 podium (2 · 1 · 3)        │ ← gold for #1       │
 *   │        └──────────────────────────────────┘                     │
 *   │                                                                 │
 *   │        Rank 4-50 list with avatars + AED + referrals            │
 *   └─────────────────────────────────────────────────────────────────┘
 *
 * Confetti respects prefers-reduced-motion.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Trophy, Sparkles, Crown, Medal, TrendingUp, Users,
} from "lucide-react";
import Avatar from "@/components/common/Avatar";

const PERIODS = [
  { id: "this_week",  label: "This week" },
  { id: "this_month", label: "This month" },
  { id: "all_time",   label: "All time" },
];

// ─── Confetti — 24 lime dots falling continuously ────────────────────────────
const Confetti = () => {
  const colors = ["#8CFF2E", "#A78BFA", "#FCD34D", "#60A5FA", "#F472B6"];
  const dots = useMemo(() => Array.from({ length: 24 }, (_, i) => ({
    left: Math.random() * 100,
    delay: -Math.random() * 4.2,
    duration: 3.6 + Math.random() * 2.4,
    color: colors[i % colors.length],
    rotate: Math.random() * 180,
  })), []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="onex-confetti absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {dots.map((d, i) => (
        <span
          key={i}
          style={{
            left: `${d.left}%`,
            background: d.color,
            animationDelay: `${d.delay}s`,
            animationDuration: `${d.duration}s`,
            transform: `rotate(${d.rotate}deg)`,
          }}
        />
      ))}
    </div>
  );
};

const PodiumCard = ({ entry, rank }) => {
  const heights = { 1: "h-44", 2: "h-32", 3: "h-28" };
  const borders = { 1: "border-[#FCD34D]", 2: "border-zinc-400", 3: "border-[#CD7F32]" };
  const accents = { 1: "#FCD34D", 2: "#D4D4D8", 3: "#CD7F32" };
  const TierIcon = rank === 1 ? Crown : Medal;
  return (
    <div className="flex flex-col items-center" data-testid={`podium-rank-${rank}`}>
      <div className="relative mb-3">
        <Avatar
          src={entry?.avatar} seed={entry?.name}
          size={rank === 1 ? 96 : 72}
          className={`!border-2 ${borders[rank]}`}
        />
        <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full grid place-items-center"
             style={{ background: accents[rank], color: "#0A0A0B" }}>
          <TierIcon size={14} strokeWidth={2.5}/>
        </div>
      </div>
      <div className="text-white text-[14px] font-semibold leading-tight">{entry?.name || "—"}</div>
      <div className="text-zinc-500 text-[11px] mb-2">{entry?.tier || "Member"}</div>
      <div className={`${heights[rank]} w-24 sm:w-32 rounded-t-2xl border-t border-x relative overflow-hidden`}
           style={{
             background: `linear-gradient(180deg, ${accents[rank]}22 0%, #0F1014 100%)`,
             borderColor: `${accents[rank]}66`,
           }}>
        <div className="absolute inset-x-0 top-2 text-center">
          <div className="text-[34px] sm:text-[42px] font-display tabular-nums" style={{ color: accents[rank] }}>{rank}</div>
          <div className="text-zinc-400 text-[12px] mt-1 tabular-nums">+AED {(entry?.balance || 0).toLocaleString()}</div>
        </div>
      </div>
    </div>
  );
};

const Leaderboard = () => {
  const { user } = useAuth();
  const [period, setPeriod] = useState("this_week");
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/leaderboard?period=${period}`).then(({ data }) => setData(data)); }, [period]);

  const entries = data?.entries || [];
  const top3 = entries.slice(0, 3);
  const rest = entries.slice(3, 50);
  // Find current user's standing
  const me = entries.find((e) => e.is_user) || {
    name: user?.name, balance: 0, referrals: 0, tier: user?.tier,
    rank: data?.user_rank || "—",
  };
  const myRank = entries.findIndex((e) => e.is_user) + 1 || data?.user_rank || "—";

  return (
    <div data-testid="leaderboard-page" className="relative pb-12">
      <Confetti />

      {/* Header */}
      <div className="relative">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#8CFF2E]">
          <Sparkles size={12}/> OneX Champions
        </div>
        <div className="flex items-end justify-between gap-4 flex-wrap mt-2">
          <div>
            <h1 className="text-4xl sm:text-5xl font-display tracking-tight text-white">Leaderboard</h1>
            <p className="text-zinc-400 text-[15px] mt-2 max-w-xl">
              Top members of OneX Club — referrals shipped, AED earned, allocations claimed.
              Climb the ranks. Become a legend.
            </p>
          </div>

          {/* My standing pill */}
          <div className="onex-card p-4 flex items-center gap-4" data-testid="my-rank-card">
            <Avatar src={user?.picture} seed={user?.name} size={48}/>
            <div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Your standing</div>
              <div className="flex items-baseline gap-2 mt-0.5">
                <span className="text-white text-[24px] font-display tabular-nums">#{myRank}</span>
                <span className="text-[#8CFF2E] text-[12px] tabular-nums">+{me.referrals || 0} refs</span>
              </div>
            </div>
          </div>
        </div>

        {/* Period tabs */}
        <div className="flex gap-2 mt-6">
          {PERIODS.map((p) => (
            <button key={p.id} onClick={() => setPeriod(p.id)}
                    data-testid={`leaderboard-period-${p.id}`}
                    className={`px-4 py-2 rounded-full text-[13px] transition-all ${
                      period === p.id
                        ? "bg-[#8CFF2E]/12 text-[#8CFF2E] border border-[#8CFF2E]/30"
                        : "text-zinc-400 hover:text-white border border-transparent"
                    }`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Podium top-3 */}
      {top3.length > 0 && (
        <div className="relative onex-card mt-8 p-6 sm:p-10 overflow-hidden" data-testid="podium">
          <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-72 h-72 rounded-full blur-3xl opacity-30 pointer-events-none"
               style={{ background: "radial-gradient(circle, #8CFF2E 0%, transparent 70%)" }}/>
          <div className="relative flex items-end justify-center gap-4 sm:gap-10">
            {top3[1] && <PodiumCard entry={top3[1]} rank={2}/>}
            {top3[0] && <PodiumCard entry={top3[0]} rank={1}/>}
            {top3[2] && <PodiumCard entry={top3[2]} rank={3}/>}
          </div>
        </div>
      )}

      {/* Ranks 4-50 list */}
      {rest.length > 0 && (
        <div className="onex-card mt-6 overflow-hidden" data-testid="leaderboard-list">
          <div className="px-5 py-3 border-b border-[#27272A] flex items-center gap-2">
            <Trophy size={14} className="text-zinc-400"/>
            <div className="text-[12px] uppercase tracking-[0.16em] text-zinc-400">Top contenders</div>
          </div>
          <ul className="divide-y divide-[#1a1b1f]">
            {rest.map((e, i) => {
              const rank = i + 4;
              return (
                <li key={e.name + rank}
                    className={`flex items-center gap-4 px-5 py-3.5 ${e.is_user ? "bg-[#8CFF2E]/8" : "hover:bg-white/[0.02]"}`}
                    data-testid={`leaderboard-row-${rank}`}>
                  <div className="w-8 text-center text-zinc-500 text-[14px] font-display tabular-nums shrink-0">{rank}</div>
                  <Avatar src={e.avatar} seed={e.name} size={36}/>
                  <div className="flex-1 min-w-0">
                    <div className={`text-[14px] font-medium leading-tight truncate ${e.is_user ? "text-[#8CFF2E]" : "text-white"}`}>
                      {e.name}{e.is_user && " · You"}
                    </div>
                    <div className="text-zinc-500 text-[11px] truncate">{e.tier || "Member"}</div>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <div className="text-right">
                      <div className="text-[11px] text-zinc-500 uppercase tracking-wider flex items-center gap-1 justify-end">
                        <Users size={10}/> Refs
                      </div>
                      <div className="text-zinc-200 text-[13px] tabular-nums">{e.referrals || 0}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[11px] text-zinc-500 uppercase tracking-wider flex items-center gap-1 justify-end">
                        <TrendingUp size={10}/> AED
                      </div>
                      <div className="text-[#8CFF2E] text-[13px] font-semibold tabular-nums">+{(e.balance || 0).toLocaleString()}</div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {entries.length === 0 && (
        <div className="onex-card mt-8 p-10 text-center" data-testid="leaderboard-empty">
          <Trophy size={32} className="text-zinc-700 mx-auto"/>
          <div className="text-white text-[16px] font-semibold mt-3">No champions this week — yet</div>
          <div className="text-zinc-500 text-[13px] mt-1">Be the first to ship a referral and claim #1.</div>
        </div>
      )}
    </div>
  );
};

export default Leaderboard;
